import { Font, Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import RobotoCyrillic from '../assets/fonts/Roboto-Regular.ttf';

Font.register({
  family: 'RobotoCyrillic',
  src: RobotoCyrillic
});

const styles = StyleSheet.create({
  page: {
    padding: 32,
    fontSize: 12,
    fontFamily: 'RobotoCyrillic',
    backgroundColor: '#fff',
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#173861',
    marginBottom: 16,
  },
  grid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 16,
    gap: 8,
  },
  gridItem: {
    width: '32%',
    marginBottom: 8,
  },
  label: {
    fontWeight: 'bold',
    color: '#173861',
  },
  value: {
    marginLeft: 4,
    color: '#222',
  },
  red: {
    color: '#d32f2f',
    fontWeight: 'bold',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#173861',
    marginBottom: 8,
    marginTop: 16,
  },
  table: {
    display: 'table',
    width: '100%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    marginBottom: 8,
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  tableHeader: {
    backgroundColor: '#f3f4f6',
    fontWeight: 'bold',
    color: '#173861',
    fontSize: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    borderBottomStyle: 'solid',
  },
  th: {
    padding: 6,
    borderRightWidth: 1,
    borderRightColor: '#e5e7eb',
    borderRightStyle: 'solid',
    textAlign: 'left',
    fontWeight: 'bold',
  },
  td: {
    padding: 6,
    borderRightWidth: 1,
    borderRightColor: '#e5e7eb',
    borderRightStyle: 'solid',
    textAlign: 'left',
    fontSize: 12,
  },
  tdRight: {
    textAlign: 'right',
  },
  lastCol: {
    borderRightWidth: 0,
  },
  totalRow: {
    fontWeight: 'bold',
    backgroundColor: '#f3f4f6',
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    borderTopStyle: 'solid',
  },
  noServices: {
    color: '#888',
    fontStyle: 'italic',
    padding: 8,
  },
});

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}`;
};

const isExpiringSoon = (dateStr) => {
  if (!dateStr) return false;
  const today = new Date();
  const expiry = new Date(dateStr);
  const diff = (expiry - today) / (1000 * 60 * 60 * 24); // days
  return diff <= 30;
};

const CarDetailsPDF = ({ car = {}, services = [], notes = [], translations = {} }) => {
  // Default English translations as fallback
  const t = (key) => translations[key] || {
    'autoPark.carInformation': 'Car Information',
    'autoPark.model': 'Model',
    'autoPark.plate': 'Plate',
    'autoPark.driver': 'Driver',
    'autoPark.contact': 'Contact',
    'autoPark.type': 'Type',
    'autoPark.status': 'Status',
    'autoPark.techInspExpiry': 'Tech Insp. Expiry',
    'autoPark.insuranceExpiry': 'Insurance Expiry',
    'autoPark.vignetteExpiry': 'Vignette Expiry',
    'autoPark.services': 'Services',
    'autoPark.description': 'Description',
    'autoPark.date': 'Date',
    'autoPark.price': 'Price',
    'autoPark.total': 'Total',
    'autoPark.notes': 'Notes',
    'autoPark.noServicesYet': 'No services yet.',
    'autoPark.noNotesYet': 'No notes yet.',
    'autoPark.active': 'Active'
  }[key] || key;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.title}>{t('autoPark.carInformation')}</Text>
        {/* Car Info Grid */}
        <View style={styles.grid}>
          <View style={styles.gridItem}><Text style={styles.label}>{t('autoPark.model')}:</Text> <Text style={styles.value}>{car.carModel || '—'}</Text></View>
          <View style={styles.gridItem}><Text style={styles.label}>{t('autoPark.plate')}:</Text> <Text style={styles.value}>{car.licensePlate || '—'}</Text></View>
          <View style={styles.gridItem}><Text style={styles.label}>{t('autoPark.driver')}:</Text> <Text style={styles.value}>{car.driverName || '—'}</Text></View>
          <View style={styles.gridItem}><Text style={styles.label}>{t('autoPark.contact')}:</Text> <Text style={styles.value}>{car.contactNumber || '—'}</Text></View>
          <View style={styles.gridItem}><Text style={styles.label}>{t('autoPark.type')}:</Text> <Text style={styles.value}>{car.carType || '—'}</Text></View>
          <View style={styles.gridItem}><Text style={styles.label}>{t('autoPark.status')}:</Text> <Text style={styles.value}>{car.status || t('autoPark.active')}</Text></View>
          <View style={styles.gridItem}><Text style={styles.label}>{t('autoPark.techInspExpiry')}:</Text> <Text style={[styles.value, isExpiringSoon(car.techInspectionExpiry) ? styles.red : null]}>{car.techInspectionExpiry ? formatDate(car.techInspectionExpiry) : '—'}</Text></View>
          <View style={styles.gridItem}><Text style={styles.label}>{t('autoPark.insuranceExpiry')}:</Text> <Text style={[styles.value, isExpiringSoon(car.insuranceExpiry) ? styles.red : null]}>{car.insuranceExpiry ? formatDate(car.insuranceExpiry) : '—'}</Text></View>
          <View style={styles.gridItem}><Text style={styles.label}>{t('autoPark.vignetteExpiry')}:</Text> <Text style={styles.value}>{car.vignetteExpiry ? formatDate(car.vignetteExpiry) : '—'}</Text></View>
        </View>
        {/* Services Table */}
        <Text style={styles.sectionTitle}>{t('autoPark.services')}</Text>
        <View style={styles.table}>
          {/* Table Header */}
          <View style={[styles.tableRow, styles.tableHeader]}>
            <Text style={[styles.th, {width: '7%'}]}>#</Text>
            <Text style={[styles.th, {width: '48%'}]}>{t('autoPark.description')}</Text>
            <Text style={[styles.th, {width: '25%'}]}>{t('autoPark.date')}</Text>
            <Text style={[styles.th, styles.lastCol, {width: '20%', textAlign: 'right'}]}>{t('autoPark.price')}</Text>
          </View>
          {/* Table Body */}
          {services.length === 0 ? (
            <View style={styles.tableRow}>
              <Text style={[styles.td, {width: '100%'}]}>{t('autoPark.noServicesYet')}</Text>
            </View>
          ) : (
            services.map((service, idx) => (
              <View key={service._id || idx} style={styles.tableRow}>
                <Text style={[styles.td, {width: '7%'}]}>{idx + 1}</Text>
                <Text style={[styles.td, {width: '48%'}]}>{service.description}</Text>
                <Text style={[styles.td, {width: '25%'}]}>{formatDateTime(service.date)}</Text>
                <Text style={[styles.td, styles.lastCol, {width: '20%', textAlign: 'right'}]}>{typeof service.price === 'number' ? service.price.toFixed(2) : ''}</Text>
              </View>
            ))
          )}
          {/* Total Row */}
          {services.length > 0 && (
            <View style={[styles.tableRow, styles.totalRow]}>
              <Text style={[styles.td, {width: '80%', textAlign: 'right'}]} colSpan={3}>{t('autoPark.total')}:</Text>
              <Text style={[styles.td, styles.lastCol, {width: '20%', textAlign: 'right'}]}>
                {services.reduce((sum, s) => sum + (typeof s.price === 'number' ? s.price : 0), 0).toFixed(2)}
              </Text>
            </View>
          )}
        </View>
        {/* Notes Table */}
        <Text style={styles.sectionTitle}>{t('autoPark.notes')}</Text>
        <View style={styles.table}>
          {/* Table Header */}
          <View style={[styles.tableRow, styles.tableHeader]}>
            <Text style={[styles.th, {width: '7%'}]}>#</Text>
            <Text style={[styles.th, {width: '68%'}]}>{t('autoPark.description')}</Text>
            <Text style={[styles.th, styles.lastCol, {width: '25%'}]}>{t('autoPark.date')}</Text>
          </View>
          {/* Table Body */}
          {notes.length === 0 ? (
            <View style={styles.tableRow}>
              <Text style={[styles.td, {width: '100%'}]}>{t('autoPark.noNotesYet')}</Text>
            </View>
          ) : (
            notes.map((note, idx) => (
              <View key={note._id || idx} style={styles.tableRow}>
                <Text style={[styles.td, {width: '7%'}]}>{idx + 1}</Text>
                <Text style={[styles.td, {width: '68%'}]}>{note.description}</Text>
                <Text style={[styles.td, styles.lastCol, {width: '25%'}]}>{formatDateTime(note.date)}</Text>
              </View>
            ))
          )}
        </View>
      </Page>
    </Document>
  );
};

export default CarDetailsPDF;
